import React from 'react';
import DOMPurify from 'dompurify';
import styles from './Icon.module.scss';

export type IconProps = Omit<
  React.HTMLAttributes<HTMLSpanElement>,
  'children'
> & {
  name: string;
  size?: string | number;
  color?: string;
};

export const Icon: React.FC<IconProps> = ({
  name,
  size,
  color,
  className,
  style,
  ...rest
}) => {
  const [svgContent, setSvgContent] = React.useState<string | null>(null);

  React.useEffect(() => {
    const loadIcon = async () => {
      try {
        const icon = await import(`../../../assets/icons/${name}.svg?raw`);
        const content = icon.default;

        const sanitizedContent = DOMPurify.sanitize(content);
        setSvgContent(sanitizedContent);
      } catch {
        console.warn(`Icon "${name}" not found`);
        setSvgContent(null);
      }
    };
    loadIcon();
  }, [name]);

  const sizeValue = typeof size === 'number' ? `${size}px` : size;

  // eslint-disable-next-line react-perf/jsx-no-new-object-as-prop
  const combinedStyle: React.CSSProperties = {
    ...style,
    ...(sizeValue && ({ '--icon-size': sizeValue } as React.CSSProperties)),
    ...(color && ({ '--icon-color': color } as React.CSSProperties)),
  };

  const innerHtml = React.useMemo(
    () => (svgContent ? { __html: svgContent } : undefined),
    [svgContent],
  );

  return (
    <span
      className={`${styles.icon} ${className || ''}`}
      style={combinedStyle}
      dangerouslySetInnerHTML={innerHtml}
      aria-hidden="true"
      {...rest}
    />
  );
};
